import webui from "../framework";
import Immutable from "immutable";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";
import { validate } from "../utils/validations";
import { extractStorePath, getEntityPath, parsePath } from "../utils/storeUtils";

export function getEntityImmutable(query = "") {
    return (dispatch, getState) => {
        let state = getState();
        let storePath = getStorePath({ query, state });
        if (!storePath) {
            return false;
        }
        return state.getIn(["data", ...storePath]);
    };
}

//extract storePath from dependencyPath,
//query can be full entityPath, partial entityPath, entityClassName, relative entityPath
// query can start with widgetPath::
//context: searching inside a single widget
//findFirst: if true returns the first result, using Depth First Search
export function getStorePath({ query, state, originEntityPath, findFirst = true, widgetId } = {}) {
    //it is possible that multiple paths be extracted
    let storePath = webui.getUtils().getDependentPaths([query], state, originEntityPath, widgetId);
    if (!storePath.length) {
        WEBUILogs.warn("Unable to find entity: " + query);
        return;
    }
    //in case of entityClassName multiple paths can be extracted
    //returns the first one
    if (findFirst && Array.isArray(storePath[0])) {
        return storePath[0];
    } else {
        return storePath;
    }
}

//extract all entity paths corresponding to an incomplete entityPath
export function getEntityPaths(query = "") {
    return (dispatch, getState) => {
        let state = getState();
        let storePaths = getStorePath({ query, state, findFirst: false });
        if (!storePaths) {
            return false;
        }
        return storePaths.map(storePath => {
            let resultPath = [];
            let currentPath = [];
            storePath.forEach(item => {
                currentPath.push(item);
                if (item !== "entities") {
                    resultPath.push(state.getIn(["data", ...currentPath, "class", 0]));
                }
            });
            return resultPath.join(".");
        });
    };
}

//validates every path
//uses the same options as validateEntity method
export function validateEntities(
    paths = [],
    widgetId,
    { silent = true, saveValidations = false, returnFailedValidations = false } = {}
) {
    return dispatch => {
        const results = paths.map(path =>
            dispatch(
                validateEntity(path, widgetId, { silent, saveValidations, returnFailedValidations })
            )
        );
        if (returnFailedValidations) {
            return results;
        } else {
            return results.every(validationFlag => validationFlag);
        }
    };
}

//validates entity
//options include:
//silen: no logging for failed validations
//saveValidations: saves failed validations in Redux store, for component highlighting
//returnFailedValidations: instead of a boolean result is return as an array of failed validations
//failedValidationsFormat:
// [
//     {
//         path:[truePath],
//         entityPath,
//         widgetId,
//         state: {
//             valid: false,
//             failedMessages: {
//                 "error": {
//                     "types": [
//                         "dateRange"
//                     ],
//                     "messages": {
//                         "dateRange": "2015 the minimum"
//                     }
//                 }
//             }
//     }
// }, ...]
export function validateEntity(
    path,
    widgetId,
    { silent = true, saveValidations = true, returnFailedValidations = false } = {}
) {
    return (dispatch, getState) => {
        let storePath = getStorePath({ query: path, state: getState(), widgetId });
        if (!storePath) {
            return [];
        }
        let entityData = dispatch(getEntity(path));
        let { validations, validationSuccess } = validate(entityData, storePath, {
            silent,
            widgetId
        });
        //@returned validation format:[{path:[truePath],state: {valid: false, failedMessages: {...}}}, ...]
        //adding entityPath and widgetId to validation results
        validations.forEach((state, index) => {
            if (state.hasOwnProperty("path")) {
                let widgetId = state.path[0]; // by default the true path first item is the widget id
                let computedBindTo = getEntityPath(state.path, getState());
                validations[index]["entityPath"] = computedBindTo;
                validations[index]["widgetId"] = widgetId;
            }
        });
        if (validations.length && saveValidations) {
            dispatch(webui.getActionByName("component.triggerMultipleSetData")(validations));
            //ui state needs container computedBindTo
            let computedPathUiState = validations.map((state, index) => {
                if (state.hasOwnProperty("path")) {
                    let widgetId = state.path[0]; // by default the true path first item is the widget id
                    let computedBindTo = getEntityPath(state.path, getState());
                    return Object.assign({}, state, { path: [widgetId, computedBindTo] });
                }
                return state;
            });
            const triggerMultipleSetUiState = webui.getActionByName(
                "component.triggerMultipleSetUiState"
            );
            dispatch(triggerMultipleSetUiState(computedPathUiState));
        }
        if (returnFailedValidations) {
            return validations;
        } else {
            return validationSuccess;
        }
    };
}

//Entity utility functions
export function hasEntity(path = "") {
    return (dispatch, getState) => {
        let state = getState();
        const { widgetPath, entityPath } = parsePath(path, getState());
        let storePath = extractStorePath(entityPath, state, widgetPath);
        if (!storePath.length) {
            return false;
        }
        return true;
    };
}
export function getEntity(path = "") {
    return dispatch => {
        let entityImmutable = dispatch(getEntityImmutable(path));
        return entityImmutable ? entityImmutable.toJS() : false;
    };
}
export function setEntity(query, entity) {
    return (dispatch, getState) => {
        let storePath = getStorePath({ query, state: getState() });
        if (!storePath) {
            return false;
        }
        let triggerSet = webui.getActionByName("component.triggerSet");
        dispatch(triggerSet(storePath, entity));
    };
}
export function addEntity(parentPath, entity) {
    return (dispatch, getState) => {
        let storePath = getStorePath({ query: parentPath, state: getState() });
        if (!storePath) {
            return false;
        }
        return dispatch({
            type: "ADD_ENTITY",
            payload: {
                target: storePath,
                data: entity
            }
        });
    };
}
function setEntityField(path = "", fieldName, fieldValue) {
    return dispatch => {
        let currentEntity = dispatch(getEntityImmutable(path));
        if (!currentEntity) {
            return;
        }
        let newEntity = currentEntity.set(fieldName, fieldValue);
        return dispatch(setEntity(path, newEntity.toJS()));
    };
}

//Entity Value utility functions
export function getEntityValue(path) {
    return getEntityProps(path, "value");
}
export function setEntityValue(path = "", value) {
    return setEntityProps(path, { value });
}

//Entity Props
export function getEntityProps(path = "", propertyName) {
    return dispatch => {
        let entityImmutable = dispatch(getEntityImmutable(path));
        let entityPropsImmutable = entityImmutable ? entityImmutable.get("properties") : false;
        if (!entityPropsImmutable) {
            return;
        }
        if (propertyName) {
            let result = entityPropsImmutable.get(propertyName);
            if (Immutable.Iterable.isIterable(result)) {
                return result.toJS();
            } else {
                return result;
            }
        }
        return entityPropsImmutable ? entityPropsImmutable.toJS() : false;
    };
}
export function setEntityProps(query = "", props) {
    return (dispatch, getState) => {
        let storePath = getStorePath({ query, state: getState() });
        if (!storePath) {
            return false;
        }
        let triggerSet = webui.getActionByName("component.triggerSet");
        dispatch(triggerSet([...storePath, "properties"], props));
    };
}

//Entity Name:
//extract entity name from entity path
export function getEntityName(entityPath = "") {
    let split = entityPath.split(".");
    return split.pop();
}

//Entity Actions
export function getEntityActions(path = "") {
    return dispatch => {
        let actions = [];
        let immutableActions;
        let entityImmutable = dispatch(getEntityImmutable(path));
        if (entityImmutable) {
            immutableActions = entityImmutable.get("actions");
            if (immutableActions) {
                actions = immutableActions.toJS();
            }
        }
        return actions;
    };
}

export function setEntityActions(path = "", actions = []) {
    return setEntityField(path, "actions", actions);
}

//Entity Children
export function getEntityChildren(path = "") {
    return dispatch => {
        let entityImmutable = dispatch(getEntityImmutable(path));
        let entities = entityImmutable ? entityImmutable.get("entities") : false;
        return entities ? entities.toJS() : false;
    };
}

export function setEntityChildren(path = "", children = []) {
    return setEntityField(path, "entities", children);
}
export function getEntityChildrenLength(path = "") {
    return dispatch => {
        let entityImmutable = dispatch(getEntityImmutable(path));
        let entities = entityImmutable ? entityImmutable.get("entities") : false;
        return entities ? entities.length : 0;
    };
}

//Entity Parent Path
//extracts parent path from full entity path
export function getParentPath(path, count = 1) {
    let result = path;
    for (let i = 0; i < count; ++i) {
        result = result.replace(/\.[^.]*$/g, "");
    }
    return result;
}

//Get UI State
export function getUiState(path) {
    return (dispatch, getState) => {
        let uiStatePathCouple = webui.getUtils().getUiStatePath(path, getState()) || [];
        let immutableResult = getState().getIn([
            "uiState",
            uiStatePathCouple[0],
            uiStatePathCouple[1]
        ]);
        if (immutableResult) {
            return immutableResult.toJS();
        } else {
            return {};
        }
    };
}

//Set UI State
export function setUiState(paths = [], uiState) {
    return (dispatch, getState) => {
        const dependencies = Array.isArray(paths) ? paths : [paths];
        const computedPathsUiState = dependencies.map(dependency => {
            const uiStatePathCouple = webui.getUtils().getUiStatePath(dependency, getState()) || [];
            return {
                path: uiStatePathCouple,
                state: uiState
            };
        });
        const triggerMultipleSetUiState = webui.getActionByName(
            "component.triggerMultipleSetUiState"
        );
        dispatch(triggerMultipleSetUiState(computedPathsUiState));
    };
}
